import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import EligibilityConditionGroup from './EligibilityConditionGroup';
import { fetchConditionFields } from '../../data';
import { getEmptyConditionValue, getGroupRowFields, getSubtreeFields } from './conditionOperators';

const DEFAULT_FIELD = 'email_subscribe';

const createCondition = (id) => ({
    id,
    field: DEFAULT_FIELD,
    operator: '=',
    value: 'true',
});

const createGroup = (id, type = 'And', condition = createCondition(`${id}-c1`)) => ({
    id,
    type,
    conditions: [condition],
    afterConditions: [],
    children: [],
});

// Ids must stay unique after rows are removed, so don't derive them from the row count.
const newRowId = (groupId, prefix = 'c') => `${groupId}-${prefix}${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

const countGroups = (items) => items.reduce(
    (count, group) => count + 1 + countGroups(group.children || []),
    0,
);

const EligibilityConditionsShared = ({ formData, updateField, showValidation = false }) => {
    const [fieldOptions, setFieldOptions] = useState([]);
    const [fieldsLoading, setFieldsLoading] = useState(true);
    const groups = formData?.eligibilityGroups?.length ? formData.eligibilityGroups : [createGroup(1)];
    const canRemoveGroup = countGroups(groups) > 1;

    useEffect(() => {
        let isMounted = true;
        fetchConditionFields()
            .then((fields) => {
                if (isMounted) setFieldOptions(fields);
            })
            .catch((err) => {
                message.error(err?.response?.data?.message || 'Failed to load condition fields');
            })
            .finally(() => {
                if (isMounted) setFieldsLoading(false);
            });
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!fieldOptions.length) return;

        let hasUpdatedValue = false;
        const initializeBooleanValues = (items) => items.map((group) => {
            const initializeRows = (rows = []) => rows.map((row) => {
                const field = fieldOptions.find((option) => option.field === row.field);
                const isBlank = row.value === '' || row.value === undefined || row.value === null;
                if (field?.type === 'boolean' && isBlank) {
                    hasUpdatedValue = true;
                    return { ...row, value: 'true' };
                }
                return row;
            });

            return {
                ...group,
                conditions: initializeRows(group.conditions),
                afterConditions: initializeRows(group.afterConditions),
                children: initializeBooleanValues(group.children || []),
            };
        });

        const nextGroups = initializeBooleanValues(groups);
        if (hasUpdatedValue) updateField('eligibilityGroups', nextGroups);
    }, [fieldOptions, formData?.eligibilityGroups, groups, updateField]);

    const updateGroups = (transform) => updateField('eligibilityGroups', transform(groups));

    const updateGroup = (groupId, changes) => {
        const walk = (items) =>
            items.map((group) => {
                if (group.id === groupId) {
                    return { ...group, ...changes, children: group.children || [] };
                }

                if (group.children && group.children.length) {
                    return {
                        ...group,
                        children: walk(group.children),
                    };
                }

                return group;
            });

        updateGroups(walk);
    };

    // New row on the first field not blocked by its group, parents or nested groups.
    const createAllowedCondition = (id, blockedFields) => {
        const option = fieldOptions.find((item) => !blockedFields.includes(item.field));
        if (!option) {
            return blockedFields.includes(DEFAULT_FIELD)
                ? { id, field: undefined, operator: '=', value: '' }
                : createCondition(id);
        }
        const operator = option.operators?.length ? option.operators[0] : '=';
        return { id, field: option.field, operator, value: getEmptyConditionValue(option.type, operator) };
    };

    // Runs `transform(group, ancestorFields)` on the matching group; ancestorFields are the
    // row fields of every parent group above it.
    const updateGroupById = (groupId, transform) => {
        const walk = (items, ancestorFields) => items.map((group) => {
            if (group.id === groupId) return transform(group, ancestorFields);
            if (!group.children?.length) return group;
            return { ...group, children: walk(group.children, [...ancestorFields, ...getGroupRowFields(group)]) };
        });
        updateGroups((items) => walk(items, []));
    };

    const addCondition = (groupId) => {
        updateGroupById(groupId, (group, ancestorFields) => {
            const blockedFields = [...ancestorFields, ...getSubtreeFields(group)];
            // Once a group has nested groups, new rows render below them.
            if (group.children?.length) {
                return {
                    ...group,
                    afterConditions: [
                        ...(group.afterConditions || []),
                        createAllowedCondition(newRowId(groupId, 'after-'), blockedFields),
                    ],
                };
            }
            return {
                ...group,
                conditions: [...group.conditions, createAllowedCondition(newRowId(groupId), blockedFields)],
            };
        });
    };

    const addNestedGroup = (groupId) => {
        updateGroupById(groupId, (group, ancestorFields) => {
            const childId = Date.now();
            const blockedFields = [...ancestorFields, ...getGroupRowFields(group)];
            return {
                ...group,
                children: [
                    ...(group.children || []),
                    createGroup(childId, 'Or', createAllowedCondition(`${childId}-c1`, blockedFields)),
                ],
            };
        });
    };

    const updateCondition = (groupId, conditionId, changes) => {
        const walk = (items) => items.map((group) => {
            if (group.id === groupId) {
                return {
                    ...group,
                    conditions: group.conditions.map((condition) => condition.id === conditionId ? { ...condition, ...changes } : condition),
                    afterConditions: (group.afterConditions || []).map((condition) => condition.id === conditionId ? { ...condition, ...changes } : condition),
                };
            }
            return { ...group, children: walk(group.children || []) };
        });

        updateGroups(walk);
    };

    const removeCondition = (groupId, conditionId) => {
        updateGroupById(groupId, (group, ancestorFields) => {
            const nextConditions = group.conditions.filter((condition) => condition.id !== conditionId);
            const nextAfterConditions = (group.afterConditions || []).filter((condition) => condition.id !== conditionId);
            const remaining = { ...group, conditions: nextConditions, afterConditions: nextAfterConditions };

            return {
                ...remaining,
                conditions: nextConditions.length
                    ? nextConditions
                    : [createAllowedCondition(newRowId(groupId), [...ancestorFields, ...getSubtreeFields(remaining)])],
            };
        });
    };

    const removeGroup = (groupId) => {
        const walk = (items) => {
            const next = items
                .filter((group) => group.id !== groupId)
                .map((group) => ({
                    ...group,
                    children: group.children ? walk(group.children) : [],
                }));

                return next;
        };

            updateGroups((items) => {
                const next = walk(items);
                return next.length ? next : [createGroup(1)];
            });
    };

    // ancestorFields: row fields of the parent groups; a group may not reuse them, nor
    // the fields of its own nested groups.
    const renderGroup = (group, canRemoveCurrentGroup, ancestorFields = []) => (
        <EligibilityConditionGroup
            key={group.id}
            group={group}
            showValidation={showValidation}
            fieldOptions={fieldOptions}
            fieldsLoading={fieldsLoading}
            blockedFields={[...ancestorFields, ...(group.children || []).flatMap(getSubtreeFields)]}
            onChangeType={(id, type) => updateGroup(id, { type })}
            onChangeCondition={updateCondition}
            onAddCondition={addCondition}
            onAddNestedGroup={addNestedGroup}
            onRemoveGroup={removeGroup}
            canRemoveGroup={canRemoveCurrentGroup}
            onRemoveCondition={removeCondition}
        >
            {group.children && group.children.length > 0 && group.children.map((child) => renderGroup(
                child,
                canRemoveGroup,
                [...ancestorFields, ...getGroupRowFields(group)],
            ))}
        </EligibilityConditionGroup>
    );

    return (
        <>
            <div className='step-container'>
                <div className='step-content'>
                    <div className='step-section-title'>
                        Attribute Conditions <span style={{ color: '#ff4d4f' }}>*</span>
                    </div>

                    {groups.map((group) => renderGroup(group, groups.length > 1))}
                </div>
            </div>
        </>
    );
};

export default EligibilityConditionsShared;
